import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, CONTACT } from "@/lib/constants";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl shadow-sm border-b border-slate-200"
          : "bg-white/80 backdrop-blur-md border-b border-transparent"
      }`}
    >
      {/* Top utility bar */}
      <div className="hidden lg:block bg-[#0B2545] text-white text-xs">
        <div className="container-fr flex justify-between items-center py-2 px-6 lg:px-12">
          <span className="tracking-wide">
            Pharmaceutical-Grade Veterinary Manufacturing • Saharanpur, India
          </span>
          <div className="flex items-center gap-6">
            <a
              href={`tel:${CONTACT.phoneTel}`}
              className="flex items-center gap-2 hover:text-[#2A9D8F] transition-colors"
              data-testid="topbar-phone"
            >
              <Phone size={12} />
              {CONTACT.phone}
            </a>
            <a
              href={`mailto:${CONTACT.email}`}
              className="hover:text-[#2A9D8F] transition-colors"
              data-testid="topbar-email"
            >
              {CONTACT.email}
            </a>
          </div>
        </div>
      </div>

      <nav className="container-fr px-6 lg:px-12 py-3 flex items-center justify-between gap-6">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src="/forage-logo.jpg"
            alt="Forage Animal Health & Nutrition"
            className="h-14 w-14 lg:h-16 lg:w-16 object-contain rounded-md group-hover:scale-105 transition-transform"
          />
          <div className="leading-tight hidden sm:block">
            <div className="font-display font-bold text-[#0B2545] text-base lg:text-lg leading-tight">
              Forage
            </div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">
              Animal Health & Nutrition
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <ul className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-wide transition-colors ${
                    isActive
                      ? "text-[#2A9D8F]"
                      : "text-[#0F172A] hover:text-[#2A9D8F]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <Link
          to="/contact"
          data-testid="navbar-cta"
          className="hidden lg:inline-flex btn-primary py-2.5 text-sm"
        >
          Request Quote
        </Link>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 rounded-md hover:bg-slate-100"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-slate-200 px-6 py-4 space-y-2 shadow-lg"
        >
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `block py-2 text-base font-medium ${
                  isActive ? "text-[#2A9D8F]" : "text-[#0F172A]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="mobile-cta"
            className="btn-primary w-full mt-3"
          >
            Request Quote
          </Link>
          <a
            href={`tel:${CONTACT.phoneTel}`}
            data-testid="mobile-phone-cta"
            className="btn-outline w-full mt-2 inline-flex items-center justify-center gap-2"
          >
            <Phone size={14} /> {CONTACT.phone}
          </a>
        </div>
      )}
    </header>
  );
}
